import { useMemo, useState } from 'react';
import type { AcceptedPayorWithNames } from '@vagusx/clients';
import { SheetHeader, SheetTitle, SheetDescription } from '@/components/ui/sheet';
import { SheetActions, SheetBody } from '@/components/custom/sheet-layout';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Separator } from '@/components/ui/separator';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { Trash2, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { getPayorClient } from '@/lib/api-clients';
import { useQueryClient } from '@tanstack/react-query';
import { queryKeys } from '@/lib/query-keys';
import {
  useAcceptedPayors,
  usePayorPlans,
  useAddAcceptedPayor,
  useRemoveAcceptedPayor,
} from '@/hooks/use-payors';

interface AcceptedPayorDetailProps {
  acceptedPayorId: string;
  onClose: () => void;
}

export function AcceptedPayorDetail({
  acceptedPayorId,
  onClose,
}: AcceptedPayorDetailProps): React.ReactElement {
  const { data: acceptedPayors } = useAcceptedPayors();
  const removeAccepted = useRemoveAcceptedPayor();
  const addAccepted = useAddAcceptedPayor();
  const queryClient = useQueryClient();
  const [isBatchUpdating, setIsBatchUpdating] = useState(false);

  const item = useMemo(
    () => acceptedPayors?.find((ap) => ap.id === acceptedPayorId),
    [acceptedPayors, acceptedPayorId]
  );

  const allAcceptedForPayor = useMemo(
    () => acceptedPayors?.filter((ap) => ap.payorId === item?.payorId) ?? [],
    [acceptedPayors, item?.payorId]
  );

  const allPlansRow = useMemo(
    () => allAcceptedForPayor.find((ap) => !ap.payorPlanId),
    [allAcceptedForPayor]
  );

  const acceptedPlanIds = useMemo(
    () => new Set(allAcceptedForPayor.filter((ap) => ap.payorPlanId).map((ap) => ap.payorPlanId!)),
    [allAcceptedForPayor]
  );

  const planIdToAcceptedId = useMemo(() => {
    const map = new Map<string, string>();
    for (const ap of allAcceptedForPayor) {
      if (ap.payorPlanId) map.set(ap.payorPlanId, ap.id);
    }
    return map;
  }, [allAcceptedForPayor]);

  const { data: catalogPlans } = usePayorPlans(item?.payorId);

  if (!item) {
    return (
      <div className="flex h-full items-center justify-center">
        <p className="text-muted-foreground">Not found</p>
      </div>
    );
  }

  const hasAllPlans = !!allPlansRow;

  const isPlanEnabled = (planId: string): boolean => {
    return hasAllPlans || acceptedPlanIds.has(planId);
  };

  /**
   * Toggle a single plan on/off.
   * When turning off a plan while "All Plans" is on:
   * - Remove "All Plans" row
   * - Add individual rows for every OTHER plan (batch, single invalidation)
   */
  const handleTogglePlan = async (planId: string, currentlyEnabled: boolean): Promise<void> => {
    if (currentlyEnabled && hasAllPlans) {
      // Batch operation: remove "all plans", add every other plan individually
      setIsBatchUpdating(true);
      try {
        const client = getPayorClient();
        await client.removeAccepted(allPlansRow!.id);
        const otherPlans = catalogPlans?.filter((p) => p.id !== planId) ?? [];
        for (const plan of otherPlans) {
          if (!acceptedPlanIds.has(plan.id)) {
            await client.addAccepted({ payorId: item.payorId, payorPlanId: plan.id });
          }
        }
        await queryClient.invalidateQueries({ queryKey: queryKeys.payors.all });
        toast.success('Plan disabled');
      } catch {
        toast.error('Failed to update plans');
        await queryClient.invalidateQueries({ queryKey: queryKeys.payors.all });
      } finally {
        setIsBatchUpdating(false);
      }
    } else if (currentlyEnabled) {
      const acceptedId = planIdToAcceptedId.get(planId);
      if (acceptedId) {
        await removeAccepted.mutateAsync(acceptedId);
      }
    } else {
      await addAccepted.mutateAsync({
        payorId: item.payorId,
        payorPlanId: planId,
      });
    }
  };

  const handleDeactivatePayor = async (): Promise<void> => {
    // Batch remove all accepted rows — use client directly to avoid per-item toasts
    setIsBatchUpdating(true);
    try {
      const client = getPayorClient();
      for (const ap of allAcceptedForPayor) {
        await client.removeAccepted(ap.id);
      }
      await queryClient.invalidateQueries({ queryKey: queryKeys.payors.all });
      toast.success('Payor deactivated');
      onClose();
    } catch {
      toast.error('Failed to deactivate payor');
      await queryClient.invalidateQueries({ queryKey: queryKeys.payors.all });
    } finally {
      setIsBatchUpdating(false);
    }
  };

  const isUpdating = isBatchUpdating || addAccepted.isPending || removeAccepted.isPending;

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <SheetHeader>
        <SheetTitle>{item.payorName}</SheetTitle>
        <SheetDescription>
          {hasAllPlans
            ? 'All plans accepted. Disable individual plans below.'
            : `${acceptedPlanIds.size} plan${acceptedPlanIds.size !== 1 ? 's' : ''} accepted.`}
        </SheetDescription>
      </SheetHeader>

      <SheetBody>
        <div className="space-y-4">
          {/* All Plans toggle */}
          <div className="flex items-center justify-between rounded-md bg-muted p-3">
            <div>
              <div className="text-sm font-medium">All Plans</div>
              <div className="text-xs text-muted-foreground">Accept every plan from this payor</div>
            </div>
            <Switch
              aria-label="Toggle all plans"
              checked={hasAllPlans}
              disabled={isUpdating}
              onCheckedChange={async (checked) => {
                if (checked) {
                  // Enable "All Plans": add the all-plans row, remove individual rows
                  setIsBatchUpdating(true);
                  try {
                    const client = getPayorClient();
                    await client.addAccepted({ payorId: item.payorId });
                    // Remove individual plan rows since "all plans" covers them
                    for (const ap of allAcceptedForPayor) {
                      if (ap.payorPlanId) {
                        await client.removeAccepted(ap.id);
                      }
                    }
                    await queryClient.invalidateQueries({ queryKey: queryKeys.payors.all });
                    toast.success('All plans enabled');
                  } catch {
                    toast.error('Failed to enable all plans');
                    await queryClient.invalidateQueries({ queryKey: queryKeys.payors.all });
                  } finally {
                    setIsBatchUpdating(false);
                  }
                } else {
                  // Disable "All Plans": remove the all-plans row
                  if (allPlansRow) {
                    await removeAccepted.mutateAsync(allPlansRow.id);
                  }
                }
              }}
            />
          </div>

          {isUpdating && (
            <div className="flex items-center justify-center gap-2 py-2 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />
              Updating plans...
            </div>
          )}

          <Separator />

          {/* Individual plans */}
          <div className="text-sm font-medium">Plans</div>
          <div className="space-y-1">
            {catalogPlans?.map((plan) => {
              const enabled = isPlanEnabled(plan.id);
              return (
                <div
                  key={plan.id}
                  className="flex items-center justify-between rounded-md px-3 py-2 text-sm"
                >
                  <div>
                    <div className="font-medium">{plan.name}</div>
                    <div className="text-xs text-muted-foreground">{plan.planType}</div>
                  </div>
                  <Switch
                    aria-label={`Toggle ${plan.name}`}
                    checked={enabled}
                    disabled={isUpdating}
                    onCheckedChange={() => handleTogglePlan(plan.id, enabled)}
                  />
                </div>
              );
            })}
            {catalogPlans?.length === 0 && (
              <p className="py-4 text-center text-sm text-muted-foreground">
                No plans in catalog for this payor.
              </p>
            )}
            {!catalogPlans && (
              <p className="py-4 text-center text-sm text-muted-foreground">Loading plans...</p>
            )}
          </div>
        </div>
      </SheetBody>

      <SheetActions
        start={
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button variant="destructive" type="button" disabled={isUpdating}>
                <Trash2 className="mr-2 h-3 w-3" />
                Deactivate Payor
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Deactivate {item.payorName}?</AlertDialogTitle>
                <AlertDialogDescription>
                  This will deactivate {item.payorName} and all its plans. It will no longer appear
                  as accepted, but historical references are preserved. You can re-add it later.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction
                  onClick={handleDeactivatePayor}
                  className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                >
                  Remove
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        }
      >
        <Button type="button" variant="outline" onClick={onClose}>
          Close
        </Button>
      </SheetActions>
    </div>
  );
}
